import React from 'react';
import Drawer from '../ui/Drawer';
import { NODE_DEFS } from '../../data/nodeDefinitions';
import { APP_EVENTS, TIME_WINDOWS } from '../../data/events';
import { segmentService } from '../../services/segmentService';
import styles from './ConfigDrawer.module.css';

function Field({ label, children }) {
  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Inp({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      className={styles.inp}
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

function Sel({ value, onChange, options }) {
  return (
    <select className={styles.sel} value={value || ''} onChange={e => onChange(e.target.value)}>
      {options.map(o => (
        typeof o === 'string'
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      className={styles.ta}
      rows={rows}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

class ConfigDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.state = { segments: [] };
  }

  componentDidMount() {
    segmentService.getAll().then((segments) => this.setState({ segments })).catch(() => {});
  }

  render() {
    const { node, open, onClose, onChange } = this.props;
    if (!node) return null;

    const def = NODE_DEFS[node.type] || {};
    const cfg = node.cfg || {};
    const set = key => val => onChange({ ...cfg, [key]: val });

    const fields = this._fields(node.type, cfg, set, this.state.segments);

    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={def.label}
        subtitle="Configure this step"
        ico={def.ico}
        icoStyle={{ background: def.icoC, color: def.icoT }}
        width="360px"
        footer={
          <button className={styles.saveBtn} onClick={onClose}>
            Done
          </button>
        }
      >
        <div className={styles.body}>
          {fields}
        </div>
      </Drawer>
    );
  }

  _fields(type, cfg, set, segments) {
    switch (type) {
      case 'entry-event':
        return (
          <>
            <Field label="Trigger Event">
              <Sel value={cfg.event} onChange={set('event')} options={APP_EVENTS} />
            </Field>
            <Field label="Filter (optional)">
              <Inp value={cfg.filter} onChange={set('filter')} placeholder="e.g. state = UP" />
            </Field>
          </>
        );

      case 'entry-schedule':
        return (
          <>
            <Field label="Cron Expression">
              <Inp value={cfg.cron} onChange={set('cron')} placeholder="0 18 * * *" />
            </Field>
            <Field label="Timezone">
              <Sel value={cfg.tz} onChange={set('tz')} options={['IST +5:30','UTC','EST −5','PST −8']} />
            </Field>
          </>
        );

      case 'entry-segment':
        return (
          <Field label="Segment">
            <Sel
              value={cfg.segmentId}
              onChange={set('segmentId')}
              options={segments.map(s => ({ value: s.id, label: s.name }))}
            />
          </Field>
        );

      case 'action-push':
        return (
          <>
            <Field label="Title">
              <Inp value={cfg.title} onChange={set('title')} placeholder="Push title" />
            </Field>
            <Field label="Body">
              <TextArea value={cfg.body} onChange={set('body')} placeholder="Push body text" />
            </Field>
            <Field label="Deep Link">
              <Inp value={cfg.link} onChange={set('link')} placeholder="app://screen" />
            </Field>
          </>
        );

      case 'action-inapp':
        return (
          <>
            <Field label="Type">
              <Sel value={cfg.type} onChange={set('type')} options={['Banner','Modal','Tooltip','Full Screen']} />
            </Field>
            <Field label="Title">
              <Inp value={cfg.title} onChange={set('title')} placeholder="Modal title" />
            </Field>
            <Field label="Body">
              <TextArea value={cfg.body} onChange={set('body')} placeholder="Modal body" />
            </Field>
            <Field label="CTA Label">
              <Inp value={cfg.cta} onChange={set('cta')} placeholder="e.g. View Offer" />
            </Field>
          </>
        );

      case 'action-whatsapp':
        return (
          <>
            <Field label="Template Name">
              <Inp value={cfg.template} onChange={set('template')} placeholder="approved_template_id" />
            </Field>
            <Field label="Variable 1">
              <Inp value={cfg.v1} onChange={set('v1')} placeholder="{{1}}" />
            </Field>
            <Field label="Variable 2">
              <Inp value={cfg.v2} onChange={set('v2')} placeholder="{{2}}" />
            </Field>
          </>
        );

      case 'action-email':
        return (
          <>
            <Field label="Subject">
              <Inp value={cfg.subject} onChange={set('subject')} placeholder="Email subject" />
            </Field>
            <Field label="Preheader">
              <Inp value={cfg.preheader} onChange={set('preheader')} placeholder="Preview text…" />
            </Field>
            <Field label="Template">
              <Sel value={cfg.template} onChange={set('template')} options={['Promotional','Transactional','Newsletter']} />
            </Field>
          </>
        );

      case 'action-sms':
        return (
          <Field label="Message">
            <TextArea value={cfg.message} onChange={set('message')} placeholder="SMS text (160 chars)" rows={4} />
          </Field>
        );

      case 'action-webhook':
        return (
          <>
            <Field label="URL">
              <Inp value={cfg.url} onChange={set('url')} placeholder="https://api.example.com/…" />
            </Field>
            <Field label="Method">
              <Sel value={cfg.method} onChange={set('method')} options={['POST','GET','PUT','PATCH']} />
            </Field>
            <Field label="Payload (JSON)">
              <TextArea value={cfg.payload} onChange={set('payload')} placeholder='{"event":"…"}' rows={4} />
            </Field>
          </>
        );

      case 'wait-relative':
        return (
          <>
            <Field label="Duration">
              <div className={styles.row2}>
                <Inp value={cfg.amount} onChange={set('amount')} placeholder="1" type="number" />
                <Sel value={cfg.unit} onChange={set('unit')} options={['minutes','hours','days','weeks']} />
              </div>
            </Field>
          </>
        );

      case 'wait-absolute':
        return (
          <>
            <Field label="Rule">
              <Sel value={cfg.rule} onChange={set('rule')} options={[
                { value: 'next_day_noon',  label: 'Next day 12 PM' },
                { value: 'next_monday',    label: 'Next Monday 9 AM' },
                { value: 'end_of_week',    label: 'End of week' },
                { value: 'custom',         label: 'Custom date/time' },
              ]} />
            </Field>
          </>
        );

      case 'logic-condition':
        return (
          <>
            <Field label="Condition">
              <Sel value={cfg.condType} onChange={set('condType')} options={[
                { value: 'event_performed',     label: 'Event performed' },
                { value: 'event_not_performed', label: 'Event NOT performed' },
                { value: 'user_property',       label: 'User property' },
              ]} />
            </Field>
            <Field label="Event">
              <Sel value={cfg.event} onChange={set('event')} options={APP_EVENTS} />
            </Field>
            <Field label="Time Window">
              <Sel value={cfg.timeWindow} onChange={set('timeWindow')} options={TIME_WINDOWS.map(tw => tw.label)} />
            </Field>
          </>
        );

      case 'logic-ab':
        return (
          <>
            <Field label="Variant A %">
              <Inp value={cfg.splitA} onChange={set('splitA')} placeholder="50" type="number" />
            </Field>
            <Field label="Variant B %">
              <Inp value={cfg.splitB} onChange={set('splitB')} placeholder="50" type="number" />
            </Field>
          </>
        );

      case 'logic-goal':
        return (
          <Field label="Goal Event">
            <Sel value={cfg.event} onChange={set('event')} options={APP_EVENTS} />
          </Field>
        );

      case 'logic-exit':
        return (
          <Field label="Exit Reason">
            <Sel value={cfg.reason} onChange={set('reason')} options={[
              { value: 'journey_complete', label: 'Journey complete' },
              { value: 'goal_achieved',    label: 'Goal achieved' },
              { value: 'unsubscribed',     label: 'User unsubscribed' },
            ]} />
          </Field>
        );

      default:
        return <div className={styles.noConfig}>No configuration needed.</div>;
    }
  }
}

export default ConfigDrawer;
